import { useEffect, useRef, useState } from 'react'
import 'maplibre-gl/dist/maplibre-gl.css'
import '../../styles/MiniMap.css'
import '../../styles/MapControls.css'
import { useMinimap } from './useMinimap'
import { useMapExport } from './useMapExport'
import { useKeyboardPan } from './useKeyboardPan'
import { useCenterToRegion } from './useCenterToRegion'
import { useMapFilters } from './useMapFilters'
import { useRuler } from './useRuler'
import { useWorldOverlays } from './useWorldOverlays'
import { useMilitaryUnits } from './useMilitaryUnits'
import { useMilPlan } from './useMilPlan'
import { useStructures } from './useStructures'
import { useInfrastructure } from './useInfrastructure'
import { useOwnedStats } from './useOwnedStats'
import { useWeather } from './useWeather'
import { useProjection } from './useProjection'
import { useMapLayers } from './useMapLayers'
import { useBorders } from './useBorders'
import { useMapInit } from './useMapInit'
import { useNativeControls } from './useNativeControls'
import { useSettingsControl } from './useSettingsControl'
import { useCoordinates } from './useCoordinates'
import { useContextMenu } from './useContextMenu'
import MapContextMenu from '../MapContextMenu'
import { useViewport } from './useViewport'
import { useMultiSelect } from './useMultiSelect'
import { useLegendIsolate } from './useLegendIsolate'
import { useBubbleMap } from './useBubbleMap'
import { useExtensionLayers } from './useExtensionLayers'
import CoordinatesDisplay from '../CoordinatesDisplay'
import { DEFAULT_OVERLAY_STYLES } from '../../utils/overlayStyles'

const MapView = ({ layers, filters, onMapLoaded, onRegionClick, selectedRegion, planMode, customColors, selectionMode, onRegionPaint, onRegionErase, onCenterToRegion, onRegionsDataLoaded, isGlobeView, satelliteMode, regionStats, statType, structFilter, weatherData, showStructures, showInfrastructure, structuresData, infrastructureData, constructionMode, onStructurePlace, onStructureMove, infrastructureMode, infrastructurePath, onInfrastructureClick, onInfraPointMove, onInfraPointDelete, onInfraPointFork, onInfraEditStart, showMilitary, militaryUnits, onUnitClick, ownedCountryId: ownedCountryIdProp, onUnitMoved, onUnitDropped, milPlanFeatures, milPlanActive, onMilPlanClick, borderWidth, borderColor, borderMode, borderOpacity, borderStyle, haloCountryIds, haloColor, mapBackground, rulerMode, rulerPath, onRulerClick, onRulerPointMove, onRulerPointDelete, organizationsCatalog, selectedOrgId, showToast, showCities, showRoads, showRailways, showBubbles, bubbleMetric, bubbleColor, bubbleSize, overlayStyles, onOpenSettings }) => {
  const styles = overlayStyles || DEFAULT_OVERLAY_STYLES
  const mapContainer = useRef(null)
  const map = useRef(null)
  const [regionsData, setRegionsData] = useState(null)
  // Bumped on zoomend so the structures clustering recomputes at the new
  // scale (clusters depend on zoom only — panning keeps relative distances).
  const [zoomBucket, setZoomBucket] = useState(2)
  const [regionsMercatorData, setRegionsMercatorData] = useState(null) // Mercator version (±85° clamp)
  const [regionsGlobeData, setRegionsGlobeData] = useState(null) // Globe version (no clamp)
  const previousCustomColorsRef = useRef({}) // Track previous colors for efficient diff (shared with useMapLayers)

  useMapInit(map, mapContainer, previousCustomColorsRef, { isGlobeView, onRegionsDataLoaded, onRegionClick, onRulerClick, onInfrastructureClick, onStructurePlace, onRegionPaint, onRegionErase, onMapLoaded, setRegionsData, setRegionsMercatorData, setRegionsGlobeData, setZoomBucket, mapBackground })

  useNativeControls(map)
  useSettingsControl(map, onOpenSettings)
  useViewport(map)
  useMultiSelect(map, regionsData)
  useLegendIsolate(map, regionsData)
  useBubbleMap(map, { showBubbles, bubbleMetric, bubbleColor, bubbleSize, regionsData })
  // Extension point (S15): future gameplay systems register layers here.
  useExtensionLayers(map, { regionsData, layers })
  const cursorCoords = useCoordinates(map)
  const { menu: contextMenu, closeMenu } = useContextMenu(map)

  // Update cursor for Plan Mode / Construction Mode / Infrastructure Mode.
  // Also focus the canvas so MapLibre's keyboard handler (arrows to pan,
  // +/- to zoom) works while drawing — dragPan is hijacked by the stroke,
  // keyboard becomes the always-available navigation.
  useEffect(() => {
    if (!map.current) return
    const canvas = map.current.getCanvas()
    canvas.style.cursor = (planMode || constructionMode || infrastructureMode || rulerMode) ? 'crosshair' : ''
    if (constructionMode || infrastructureMode || rulerMode) {
      map.current.keyboard.enable()
      canvas.focus()
    }
  }, [planMode, constructionMode, infrastructureMode, rulerMode])
  
  useBorders(map, { satelliteMode, isGlobeView, borderColor, borderWidth, borderMode, borderOpacity, borderStyle, haloCountryIds, haloColor, mapBackground, regionsData })

  // Ocean/background colour follows the theme (whitish in light mode).
  useEffect(() => {
    if (!map.current) return
    const apply = () => {
      if (map.current.getLayer('background')) {
        map.current.setPaintProperty('background', 'background-color', mapBackground || '#1a1a1a')
      }
    }
    if (map.current.isStyleLoaded()) apply()
    else map.current.once('idle', apply)
  }, [mapBackground])

  useProjection(map, mapContainer, { isGlobeView, regionsMercatorData, regionsGlobeData, onRegionsDataLoaded, setRegionsData, mapBackground })

  useMapLayers(map, previousCustomColorsRef, { regionsData, planMode, customColors, layers, statType, regionStats, organizationsCatalog, selectedOrgId })



  useOwnedStats(map, { regionStats, structFilter, regionsData, regionsMercatorData, regionsGlobeData })

  useWeather(map, { weatherData, weatherLayerActive: layers.weather, planMode, regionsData, regionsMercatorData, regionsGlobeData })

  useStructures(map, { showStructures, constructionMode, infrastructureMode, structuresData, zoomBucket, onStructureMove, regionsData })

  // Topography layer: raster visibility + region fill fade so the relief
  // shows through while regions stay clickable.
  useEffect(() => {
    if (!map.current || !map.current.getLayer('topography')) return
    const on = !!layers.topography
    map.current.setLayoutProperty('topography', 'visibility', on ? 'visible' : 'none')
    if (map.current.getLayer('regions-fill')) {
      map.current.setPaintProperty('regions-fill', 'fill-opacity', on ? 0.08 : 0.8)
    }
  }, [layers.topography])

  useMilitaryUnits(map, mapContainer, { showMilitary, militaryUnits, onUnitClick, onUnitMoved, onUnitDropped })

  useMilPlan(map, { milPlanFeatures, milPlanActive, onMilPlanClick })

  useInfrastructure(map, { showInfrastructure, infrastructureMode, constructionMode, infrastructureData, infrastructurePath, onInfraEditStart, onInfraPointMove, onInfraPointDelete, onInfraPointFork, styles, regionsData })

  useWorldOverlays(map, { showCities, showRoads, showRailways, styles, regionsData })


  useRuler(map, { rulerMode, rulerPath, onRulerPointMove, onRulerPointDelete })

  // Filters, PNG export, keyboard panning, fly-to-region, and the minimap are
  // each self-contained — extracted into single-responsibility hooks.
  useMapFilters(map, regionsData, filters)
  useMapExport(map, showToast)
  useKeyboardPan(map)
  useCenterToRegion(map, regionsData, onCenterToRegion)
  const { minimapCanvasRef, minimapVisible, handleMinimapClick } = useMinimap(map, regionsData, mapBackground)

  return (
    <>
      <div
        ref={mapContainer}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%'
        }}
      />
      <canvas
        ref={minimapCanvasRef}
        width={180}
        height={110}
        className="minimap-canvas"
        style={{ display: minimapVisible ? 'block' : 'none' }}
        onClick={handleMinimapClick}
        title="Cliquer pour se déplacer"
      />
      <CoordinatesDisplay coords={cursorCoords} />
      <MapContextMenu menu={contextMenu} onClose={closeMenu} map={map} showToast={showToast} />
    </>
  )
}

export default MapView
